// This component implements actual audio recording with browser APIs, retrieving/storing it, and showing the
// existing recordings, if any, with controls to play it back or delete.  It does NOT include controls to actually
// start or stop the recording, this should be done by its parent through the handle (see IHandle); if there is
// no existing recording yet, this component will just render nothing.  Currently TextInput has start/stop recording
// functionality (microphone button) but other components also could.
import { forwardRef, Ref, useImperativeHandle, useRef, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { v4 as uuid } from 'uuid';

import { TranslationsKeys } from 'types/translation';
import { AUDIO_CATEGORY } from 'types/attachmentTypes';
import { Attachment } from 'types/surveyResponse';
import { useTranslate } from 'hooks/useTranslate';
import { useBlobDataUrlQuery } from 'hooks/queries';
import { useBlobMutation, useDeleteBlobMutation } from 'hooks/mutations';
import { Modal } from 'components/Modal';
import { MessageBar } from 'components/MessageBar';
import { getBlobHotlinkUrl } from 'utils/blob';
import { AudioPlayer } from './AudioPlayer';

const MAX_LENGTH = 59500; // ms
const GRANULARITY = 5000; // ms

interface IProps {
  label?: string;
  audios?: Attachment[];
  onChange: (audio: Attachment[]) => void;
}

export interface IHandle {
  startRecording: () => void;
  stopRecording: () => void;
}

// for internal state
interface IRecordingState {
  mediaRecorder: MediaRecorder;
  startTimestamp: DOMHighResTimeStamp;
  recordedChunks: Blob[];
}

const extensions = {
  'audio/webm': '.webm',
  'audio/ogg': '.ogg',
  'audio/way': '.wav'
};

const AudioPlayerWrapper = ({ label, audio, onDelete }: { label: string | undefined, audio: Attachment, onDelete: () => void }) => {
  const audioDataUrlQuery = useBlobDataUrlQuery(AUDIO_CATEGORY, audio.name);
  return (
    <AudioPlayer
      label={label}
      createdAt={audio.createdAt!}
      src={audioDataUrlQuery.data ?? getBlobHotlinkUrl(AUDIO_CATEGORY, audio.name)}
      duration={audio.audioLength! / 1000}
      isLoading={audioDataUrlQuery.isLoading}
      isError={audioDataUrlQuery.isError}
      onDelete={onDelete}
    />
  );
};

export const Audio = forwardRef(({ label, audios, onChange }: IProps, ref: Ref<IHandle>) => {
  // this does not need to be actual React state, just keep it in a ref
  const recordingStateRef = useRef<IRecordingState>();
  const [audioError, setAudioError] = useState<TranslationsKeys | undefined>(undefined);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<Attachment | undefined>(undefined);
  const { t } = useTranslate();
  const audioBlobMutation = useBlobMutation();
  const audioDeleteBlobMutation = useDeleteBlobMutation();

  useImperativeHandle(
    ref,
    () => ({
      startRecording: async () => {
        try {
          // if recording is still somehow already in progress, abort it, discarding anything we might have had
          if (recordingStateRef.current) {
            recordingStateRef.current.mediaRecorder.ondataavailable = null;
            recordingStateRef.current.mediaRecorder.onstop = null;
            recordingStateRef.current.mediaRecorder.stop();
          }

          // check that the necessary browser API is present at all
          // it's been available for a long time but this still might happen e.g. with the app running in
          // "insecure context" (over http not on localhost, which might happen with local development)
          if (!('mediaDevices' in navigator)) {
            setAudioError(TranslationsKeys.audio_error_mediaDeviceAPIMissing);
            return;
          }

          // request audio recording stream, using all defaults, so no preferences on codec etc.
          // this will trigger the browser permission prompt, if that hasn't happened before
          let mediaStream;
          try {
            // try to detect if the call took more than one second
            // if it did, this probably means that permission prompt had to be triggered
            // in this case, do nothing and do not start recording, user will need to retry again
            // otherwise, recording would have continued in background but the user would
            // already have let go of the "recording" icon, so app state would get inconsistent 
            const started = performance.now();
            mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            const elapsed = performance.now() - started;
            if (elapsed > 1000) {
              return;
            }
          } catch (e) {
            if (e instanceof DOMException) {
              if (e.name === 'NotFoundError') {
                // no microphone
                setAudioError(TranslationsKeys.audio_error_mediaDeviceNotFound);
                return;
              } else if (e.name === 'NotAllowedError' || e.name === 'SecurityError') {
                // disallowed by user
                setAudioError(TranslationsKeys.audio_error_notAllowed);
                return;
              } else {
                throw e;
              }
            } else {
              throw e;
            }
          }

          // create MediaRecorder object and store necessary state in a ref
          recordingStateRef.current = {
            mediaRecorder: new MediaRecorder(mediaStream),
            startTimestamp: 0,
            recordedChunks: []
          };

          // store timestamp when the recording was started
          recordingStateRef.current.mediaRecorder.onstart = (e: Event) => {
            recordingStateRef.current!.startTimestamp = e.timeStamp;
          };

          // called whenever at most GRANULARITY ms of audio is recorded
          recordingStateRef.current.mediaRecorder.ondataavailable = (e: BlobEvent) => {
            if (recordingStateRef.current) {
              // add a blob to the recorder array
              if (e.data.size > 0) {
                recordingStateRef.current.recordedChunks.push(e.data);
              }
              // stop if max length exceeded
              if (e.timeStamp - recordingStateRef.current.startTimestamp >= MAX_LENGTH) {
                recordingStateRef.current.mediaRecorder.stop();
              }
            }
          };

          // process the recorded audio
          recordingStateRef.current.mediaRecorder.onstop = async (e: Event) => {
            try {
              if (recordingStateRef.current) {
                // discard recordings of less than 2 sec
                const audioLength = Math.trunc(e.timeStamp - recordingStateRef.current.startTimestamp);
                if (audioLength >= 2000) {
                  // concatenate chunks into a blob with the full recording
                  // yes, they are supposed to be just concatenated
                  const fullBlob = new Blob(recordingStateRef.current.recordedChunks, {
                    type: recordingStateRef.current.mediaRecorder.mimeType
                  });
                  // generate name
                  let name = uuid();
                  for (const [mime, extension] of Object.entries(extensions)) {
                    if (fullBlob.type.startsWith(mime)) {
                      name += extension;
                    }
                  }
                  // actually store blob
                  await audioBlobMutation.mutateAsync({
                    category: AUDIO_CATEGORY,
                    blobId: name,
                    blob: fullBlob
                  });
                  // issue final event, which will store recording metadata to survey response
                  onChange([...(audios || []), {
                    name,
                    audioLength,
                    createdAt: new Date().toISOString(),
                    mimeType: recordingStateRef.current.mediaRecorder.mimeType
                  }]);
                }
                // reset state
                recordingStateRef.current = undefined;
              }
            } catch (e) {
              setAudioError(TranslationsKeys.audio_error_saveError);
            }
          };

          // start recording after everything is set up
          recordingStateRef.current.mediaRecorder.start(GRANULARITY);
        } catch (e) {
          setAudioError(TranslationsKeys.audio_error_unknown);
          recordingStateRef.current = undefined;
        }
      },

      stopRecording: () => {
        // most of the work will be done by MediaRecorder onstop handler
        recordingStateRef.current?.mediaRecorder.stop();
      }
    }),
    [onChange]
  );
  
  const deleteAudio = async (audio?: Attachment) => {
    if (audio) {
      setShowDeleteConfirmation(undefined);
      onChange(audios!.filter(a => a !== audio));
      await audioDeleteBlobMutation.mutateAsync(audio?.name);
    }
  };

  return (
    <>
      {audioError && <MessageBar variant="danger" title="" description={t(audioError)} showCloseAction={false} />}
      {(audios || []).map((a, k) => <AudioPlayerWrapper
        key={a.name}  
        label={(audios?.length ?? 0) > 1 ? `${k+1}. ${label}` : label} 
        audio={a}
        onDelete={() => setShowDeleteConfirmation(a)}
      />)}
      {showDeleteConfirmation && (
        <Modal
          title={t(TranslationsKeys.audio_deleteConfirmTitle)}
          description={
            <>
              {t(TranslationsKeys.audio_deleteConfirmText1)}
              <strong>{label}</strong>
              {t(TranslationsKeys.audio_deleteConfirmText2)}
            </>
          }
          primaryButtonLabel={
            <div className="d-flex justify-content-center align-items-center gap-2">
              <Trash2 />
              {t(TranslationsKeys.common_delete)}
            </div>
          }
          primaryButtonVariant="danger"
          secondaryButtonLabel={t(TranslationsKeys.common_close)}
          onPrimaryClick={() => deleteAudio(showDeleteConfirmation)}
          onSecondaryClick={() => setShowDeleteConfirmation(undefined)}
        />
      )}
    </>
  );
});
